import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import type {
  BugDraft,
  ChatMessage,
  FailureCategory,
  TestReview,
  Verdict,
} from '@sr-test-wizard/shared';
import { VERDICT_CSS, VERDICT_LABEL, filterRequirementsForJurisdictions } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { runAssetsApi } from '../../lib/runAssets';
import { runVideosApi } from '../../lib/runVideosApi';
import { jiraWikiToMarkdown } from '../../lib/jiraWiki';
import { VerdictPicker } from '../../components/VerdictPicker';
import { AIChat } from '../../components/AIChat';
import { FramesZoomModal, type FramesZoomItem } from '../../components/FramesZoomModal';
import { ScoutFindingsPanel } from './ScoutFindingsPanel';
import { BugBuilderModal } from '../bug-dialog/BugBuilderModal';
import { useRunStore } from '../../state/runStore';
import { JiraKeyLink } from '../../components/JiraKeyLink';
import { RelatedRequirementsSection } from './RelatedRequirementsSection';

interface Props {
  runId: string;
  test: TestReview;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

/**
 * Per-test detail pane. Now renders inline image thumbnails for every screenshot
 * the AI cited (`evidence.images[].name`) and a side-by-side layout where the
 * source-code excerpt sits next to the first cited screenshot, matching the
 * QAmlet pattern from the Sneaky Project Phase 5 mockup.
 */
export function TestDetailPane({ runId, test, onPrev, onNext, hasPrev, hasNext }: Props) {
  const applyTestUpdate = useRunStore((s) => s.applyTestUpdate);
  const analyzingTestKeys = useRunStore((s) => s.analyzingTestKeys);
  const scoutProgress = useRunStore((s) => s.scoutProgress[test.test_key] ?? null);
  const setTestAnalyzing = useRunStore((s) => s.setTestAnalyzing);
  const currentRun = useRunStore((s) => s.current);
  const texec = useRunStore((s) => s.texec);
  const gameName = currentRun?.game_name ?? '';
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const analyzing = analyzingTestKeys.includes(test.test_key);
  const [bugOpen, setBugOpen] = useState(false);
  const [bugDraft, setBugDraft] = useState<BugDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [savingPatch, setSavingPatch] = useState(false);
  const [justification, setJustification] = useState(test.tester_justification ?? '');
  const [notes, setNotes] = useState(test.tester_notes ?? '');
  // Failure category captured when the tester overrides the AI verdict. Drives
  // the eval-loop correction capture (recognition / observation / judgment /
  // data). Local-only until an override + category are both set.
  const [failureCategory, setFailureCategory] = useState<FailureCategory | ''>('');
  // Zoom modal can be opened from a single thumbnail (1-element sequence) OR
  // from a frame inside the range modal (sequence = every frame the range
  // modal showed, plus the current index, so the user can prev/next without
  // having to bounce back to the grid).
  const [zoomItems, setZoomItems] = useState<FramesZoomItem[] | null>(null);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [rangeView, setRangeView] = useState<
    | { slug: string; fromSecond: number; toSecond: number; rationale: string }
    | null
  >(null);

  useEffect(() => {
    setJustification(test.tester_justification ?? '');
    setNotes(test.tester_notes ?? '');
    setFailureCategory('');
    api
      .get<{ review: TestReview; chat: ChatMessage[] }>(`/api/runs/${runId}/tests/${test.test_key}`)
      .then((data) => setChat(data.chat))
      .catch((err) => setError((err as Error).message));
  }, [runId, test.test_key, test.tester_justification, test.tester_notes]);

  const effective = test.tester_verdict ?? test.ai_verdict;

  const analysisInputs = useRunStore((s) => s.analysisInputs);
  const analyze = async (opts: { withChat?: boolean } = {}) => {
    setError(null);
    setTestAnalyzing(test.test_key, true);
    try {
      await api.post(`/api/runs/${runId}/tests/${test.test_key}/analyze`, {
        ...(analysisInputs ? { inputs: analysisInputs } : {}),
        ...(opts.withChat ? { withChat: true } : {}),
      });
    } catch (err) {
      setError((err as Error).message);
      setTestAnalyzing(test.test_key, false);
    }
  };
  // Whether the persisted chat for this test has at least one tester message.
  // We use it to decide if the "Re-analyze with chat" affordance is meaningful.
  // Note: AIChat owns its own message list once mounted (the initial set comes
  // from `chat`), so this can lag by one round-trip - the chat composer button
  // below stays accurate because it derives from the AIChat-internal state.
  const hasChatGuidance = chat.some((m) => m.role === 'user');

  const draftBugFromChat = async () => {
    setDrafting(true);
    setError(null);
    try {
      const draft = await api.post<BugDraft>(
        `/api/runs/${runId}/tests/${test.test_key}/bug/draft`,
      );
      setBugDraft(draft);
      setBugOpen(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  const openBlankBug = () => {
    setBugDraft(null);
    setBugOpen(true);
  };

  const patch = async (body: {
    tester_verdict?: Verdict;
    tester_justification?: string;
    tester_notes?: string;
    failure_category?: FailureCategory;
    finalize?: boolean;
  }) => {
    setSavingPatch(true);
    setError(null);
    try {
      const updated = await api.patch<TestReview>(
        `/api/runs/${runId}/tests/${test.test_key}`,
        body,
      );
      applyTestUpdate(test.test_key, updated);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPatch(false);
    }
  };

  const stepsBlock = useMemo(() => {
    if (!test.test_steps?.length) return null;
    return (
      <ol style={{ paddingLeft: 20 }}>
        {test.test_steps.map((step, idx) => (
          <li key={idx}>
            {step.action && (
              <div>
                <strong>Action:</strong> {step.action}
              </div>
            )}
            {step.data && (
              <div>
                <strong>Data:</strong> {step.data}
              </div>
            )}
            {step.result && (
              <div>
                <strong>Expected:</strong> {step.result}
              </div>
            )}
          </li>
        ))}
      </ol>
    );
  }, [test.test_steps]);

  const codeEvidence = test.ai_assessment?.evidence?.code ?? [];
  const imageEvidence = test.ai_assessment?.evidence?.images ?? [];

  // An evidence "image" can either be a Phase-4 game-rules screenshot or a
  // gameplay-video frame (when the AI sets `video_slug` + `second`, with an
  // optional `second_end` for a range). Build the right URL + caption +
  // range descriptor so the thumbnail strip, range modal, and single-image
  // zoom all render correctly off the same source of truth.
  function formatClock(sec: number): string {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
  function videoFrameUrl(slug: string, second: number): string {
    return runVideosApi.frameUrl(runId, slug, `frame_${String(second).padStart(5, '0')}.jpg`);
  }
  function resolveEvidenceImage(img: {
    name: string;
    video_slug?: string;
    second?: number;
    second_end?: number;
  }): {
    url: string;
    caption: string;
    isVideo: boolean;
    range: { slug: string; fromSecond: number; toSecond: number } | null;
  } {
    if (img.video_slug && typeof img.second === 'number') {
      const start = img.second;
      const end = typeof img.second_end === 'number' && img.second_end > start ? img.second_end : start;
      const url = videoFrameUrl(img.video_slug, start);
      const caption =
        end > start
          ? `${img.video_slug} @ ${formatClock(start)} - ${formatClock(end)}`
          : `${img.video_slug} @ ${formatClock(start)}`;
      return {
        url,
        caption,
        isVideo: true,
        range:
          end > start
            ? { slug: img.video_slug, fromSecond: start, toSecond: end }
            : null,
      };
    }
    return {
      url: runAssetsApi.imageUrl(runId, img.name),
      caption: img.name,
      isVideo: false,
      range: null,
    };
  }

  // Renders one evidence thumbnail (game-rules screenshot OR gameplay-video
  // frame / range). Shared by the new per-category sections and the legacy
  // fallback strip so the range modal + zoom behaviour stays identical.
  type EvidenceImage = (typeof imageEvidence)[number];
  function renderThumb(img: EvidenceImage, key: string) {
    const resolved = resolveEvidenceImage(img);
    const onClick = () => {
      if (resolved.range) {
        setRangeView({
          slug: resolved.range.slug,
          fromSecond: resolved.range.fromSecond,
          toSecond: resolved.range.toSecond,
          rationale: img.relevant_text,
        });
      } else {
        setZoomItems([{ url: resolved.url, caption: resolved.caption }]);
        setZoomIndex(0);
      }
    };
    return (
      <button
        key={key}
        className="thumb-item"
        onClick={onClick}
        title={
          resolved.range
            ? `Click to view all ${resolved.range.toSecond - resolved.range.fromSecond + 1} frames in this range`
            : img.relevant_text
        }
      >
        <div className="thumb-image-wrap">
          <img
            src={resolved.url}
            alt={resolved.caption}
            loading="lazy"
            onError={(e) => {
              const node = e.currentTarget as HTMLImageElement;
              node.style.display = 'none';
              const sib = node.nextElementSibling as HTMLElement | null;
              if (sib) sib.style.display = 'flex';
            }}
          />
          <div
            className="thumb-fallback"
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              border: '1px dashed var(--border)',
              borderRadius: 4,
              color: 'var(--muted)',
              fontSize: 11,
              minHeight: 80,
            }}
          >
            {resolved.isVideo ? `frame ${formatClock(img.second ?? 0)}` : 'image missing'}
          </div>
        </div>
        <div className="thumb-caption">
          {resolved.isVideo ? (
            <span className="badge small" style={{ marginRight: 4 }}>
              {resolved.range ? 'video range' : 'video'}
            </span>
          ) : null}
          <code className="mono small">{resolved.caption}</code>
          <div className="thumb-text">{img.relevant_text}</div>
        </div>
      </button>
    );
  }

  // Split cited images into Phase-4 game-rules screenshots vs gameplay-video
  // frames, and group the video frames by their slug so the "Supported by
  // Video(s)" section can show one sub-block per clip.
  const gameRulesImages = imageEvidence.filter((img) => !img.video_slug);
  const videoFramesBySlug = new Map<string, EvidenceImage[]>();
  for (const img of imageEvidence) {
    if (!img.video_slug) continue;
    const arr = videoFramesBySlug.get(img.video_slug) ?? [];
    arr.push(img);
    videoFramesBySlug.set(img.video_slug, arr);
  }
  const evidenceSummary = test.ai_assessment?.evidence_summary;
  const summaryVideos = evidenceSummary?.videos ?? [];
  // Union of slugs the model reasoned about and slugs that actually have
  // cited frames, preserving the model's ordering first.
  const videoSlugs = Array.from(
    new Set<string>([...summaryVideos.map((v) => v.slug), ...videoFramesBySlug.keys()]),
  );
  // Use the new per-category layout only when the model populated
  // `evidence_summary` (new assessments); older persisted reviews fall back
  // to the legacy combined evidence grid so nothing regresses.
  const hasEvidenceSummary =
    !!evidenceSummary &&
    ((evidenceSummary.source_code ?? '').trim().length > 0 ||
      (evidenceSummary.game_rules_images ?? '').trim().length > 0 ||
      summaryVideos.length > 0);
  const showSourceCodeSection =
    hasEvidenceSummary &&
    ((evidenceSummary?.source_code ?? '').trim().length > 0 || codeEvidence.length > 0);
  const showGameRulesSection =
    hasEvidenceSummary &&
    ((evidenceSummary?.game_rules_images ?? '').trim().length > 0 || gameRulesImages.length > 0);
  const showVideosSection = hasEvidenceSummary && videoSlugs.length > 0;

  const hasAssessment = !!test.ai_assessment;
  const analyzeLabel = analyzing
    ? hasAssessment
      ? 'Re-analyzing...'
      : 'Analyzing...'
    : hasAssessment
      ? 'Re-analyze'
      : 'Analyze';

  return (
    <div className="detail">
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button className="btn" disabled={!hasPrev} onClick={onPrev}>
          {'\u2190 Prev'}
        </button>
        <button className="btn" disabled={!hasNext} onClick={onNext}>
          {'Next \u2192'}
        </button>
        <button
          className={`btn small ${hasAssessment ? '' : 'btn-primary'}`}
          disabled={analyzing}
          onClick={() => void analyze()}
          title={hasAssessment ? 'Re-run the AI for this test only' : 'Run the AI for this test only'}
        >
          {analyzeLabel}
        </button>
        {hasAssessment && hasChatGuidance && (
          <button
            className="btn small"
            disabled={analyzing}
            onClick={() => void analyze({ withChat: true })}
            title="Re-run the AI with your chat conversation included as authoritative human guidance"
          >
            {analyzing ? 'Re-analyzing...' : 'Re-analyze with chat'}
          </button>
        )}
        <span className="spacer" />
        <JiraKeyLink keyId={test.test_key} className="badge mono small" />
        {test.xray_status && <span className="badge">{test.xray_status}</span>}
        {test.in_game_play && (
          <span
            className="badge"
            title={`Played live via Look at that on ${new Date(test.in_game_play.playedAt).toLocaleString()}: ${test.in_game_play.rationale}`}
          >
            <span
              className={`dot ${VERDICT_CSS[test.in_game_play.verdict]}`}
              style={{ marginRight: 4 }}
            />
            live play: {VERDICT_LABEL[test.in_game_play.verdict]}
          </span>
        )}
        {(test.bug_keys ?? []).map((bk) => (
          <JiraKeyLink
            key={bk}
            keyId={bk}
            className="badge bug"
            title={`Open bug ${bk} in Jira`}
          />
        ))}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '8px 12px',
            border: '1px solid var(--bad)',
            background: 'rgba(248, 81, 73, 0.08)',
            borderRadius: 6,
            color: 'var(--bad)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <strong style={{ flex: '0 0 auto' }}>Re-analyze failed:</strong>
          <span style={{ whiteSpace: 'pre-wrap' }}>{error}</span>
          <button
            type="button"
            className="btn-ghost small"
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', color: 'var(--bad)' }}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <h2>{test.test_summary}</h2>
      {test.test_labels.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {test.test_labels.map((label) => (
            <span key={label} className="badge">
              {label}
            </span>
          ))}
        </div>
      )}

      <h3>Description</h3>
      {test.test_description ? (
        <div className="ai-card jira-description">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noreferrer noopener" />
              ),
            }}
          >
            {jiraWikiToMarkdown(test.test_description)}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="muted small">(no description on Jira)</p>
      )}

      {stepsBlock && (
        <>
          <h3>Xray steps</h3>
          {stepsBlock}
        </>
      )}

      <RelatedRequirementsSection requirements={test.related_requirements ?? []} />

      <h3>AI assessment</h3>
      {analyzing && (
        <div className="ai-analyzing-banner" role="status" aria-live="polite">
          <span className="ai-analyzing-spinner" aria-hidden="true" />
          <div className="flex-col" style={{ gap: 2 }}>
            <strong>Analyzing {test.test_key}...</strong>
            <span className="muted small">
              GPT is reading the source, screenshots and steps. This usually takes 20-40s.
            </span>
            {scoutProgress && (
              <span className="muted small">
                {scoutProgress.status === 'cached' ? 'Cached' : 'Scouting'}{' '}
                {scoutProgress.current}/{scoutProgress.total}: <code className="mono">{scoutProgress.filename}</code>
                {scoutProgress.status === 'failed' ? ' (failed)' : ''}
              </span>
            )}
          </div>
        </div>
      )}
      {test.ai_assessment ? (
        <div className="ai-card">
          <div className="row">
            <span className={`dot ${VERDICT_CSS[test.ai_assessment.verdict]}`} />
            <strong>{VERDICT_LABEL[test.ai_assessment.verdict]}</strong>
            <span className="badge">confidence: {test.ai_assessment.confidence}</span>
            {test.ai_assessment.game_math_relevance &&
              test.ai_assessment.game_math_relevance !== 'none' && (
                <span
                  className="chip small good"
                  title="Game Knowledge and/or Stake Engine math context was injected for this verdict"
                >
                  {test.ai_assessment.game_math_relevance === 'investigate'
                    ? 'Game/math + drill-down'
                    : 'Game/math context'}
                </span>
              )}
            <span className="spacer" />
            <button className="btn btn-ghost small" onClick={() => void analyze()} disabled={analyzing}>
              {analyzing ? 'Re-analyzing...' : 'Re-analyze'}
            </button>
            {hasChatGuidance && (
              <button
                className="btn btn-ghost small"
                onClick={() => void analyze({ withChat: true })}
                disabled={analyzing}
                title="Re-run the AI with your chat conversation as authoritative human guidance"
              >
                {analyzing ? 'Re-analyzing...' : 'Re-analyze with chat'}
              </button>
            )}
          </div>
          <p style={{ marginTop: 8 }}>{test.ai_assessment.rationale}</p>
          {test.ai_assessment.steps_supported_by_code.length > 0 && (
            <>
              <strong>Supported steps:</strong>
              <ul>
                {test.ai_assessment.steps_supported_by_code.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </>
          )}
          {test.ai_assessment.steps_needing_in_game.length > 0 && (
            <>
              <strong>Needs in-game verification:</strong>
              <ul>
                {test.ai_assessment.steps_needing_in_game.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </>
          )}

          {hasEvidenceSummary ? (
            <div className="evidence-sections" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {showSourceCodeSection && (
                <div className="evidence-section">
                  <strong>Supported by Source Code</strong>
                  {(evidenceSummary?.source_code ?? '').trim() && (
                    <p style={{ marginTop: 4 }}>{evidenceSummary?.source_code}</p>
                  )}
                  {codeEvidence.map((entry, i) => (
                    <div key={i} className="evidence-code-block">
                      <code>{entry.file}</code>
                      {entry.lines && (
                        <span className="muted small">
                          {' '}
                          lines {entry.lines[0]}-{entry.lines[1]}
                        </span>
                      )}
                      <pre>{entry.excerpt}</pre>
                    </div>
                  ))}
                </div>
              )}
              {showGameRulesSection && (
                <div className="evidence-section">
                  <strong>Supported by Game Rules images</strong>
                  {(evidenceSummary?.game_rules_images ?? '').trim() && (
                    <p style={{ marginTop: 4 }}>{evidenceSummary?.game_rules_images}</p>
                  )}
                  {gameRulesImages.length > 0 && (
                    <div className="thumb-strip">
                      {gameRulesImages.map((img, i) => renderThumb(img, `${img.name}-${i}`))}
                    </div>
                  )}
                </div>
              )}
              {showVideosSection && (
                <div className="evidence-section">
                  <strong>Supported by Video(s)</strong>
                  {videoSlugs.map((slug) => {
                    const meta = summaryVideos.find((v) => v.slug === slug);
                    const frames = videoFramesBySlug.get(slug) ?? [];
                    return (
                      <div key={slug} className="video-evidence-group" style={{ marginTop: 8 }}>
                        <code className="mono small">{slug}</code>
                        {meta?.reasoning?.trim() && (
                          <p style={{ marginTop: 4 }}>{meta.reasoning}</p>
                        )}
                        {frames.length > 0 && (
                          <div className="thumb-strip">
                            {frames.map((img, i) => renderThumb(img, `${slug}-${img.second ?? i}-${i}`))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            (codeEvidence.length > 0 || imageEvidence.length > 0) && (
              <div className="evidence-grid" style={{ marginTop: 12 }}>
                {codeEvidence.length > 0 && (
                  <div className="evidence-col">
                    <strong>Code evidence</strong>
                    {codeEvidence.map((entry, i) => (
                      <div key={i} className="evidence-code-block">
                        <code>{entry.file}</code>
                        {entry.lines && (
                          <span className="muted small">
                            {' '}
                            lines {entry.lines[0]}-{entry.lines[1]}
                          </span>
                        )}
                        <pre>{entry.excerpt}</pre>
                      </div>
                    ))}
                  </div>
                )}
                {imageEvidence.length > 0 && (
                  <div className="evidence-col">
                    <strong>Screenshot evidence</strong>
                    <div className="thumb-strip">
                      {imageEvidence.map((img, i) => renderThumb(img, `${img.name}-${i}`))}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      ) : (
        <div className="ai-card">
          <p className="muted">No AI assessment yet.</p>
          <button className="btn btn-primary" disabled={analyzing} onClick={() => void analyze()}>
            {analyzing ? 'Analyzing...' : 'Analyze this test'}
          </button>
        </div>
      )}

      {test.ai_assessment?.game_math_findings && (
        <details className="ai-card" style={{ marginTop: 8 }}>
          <summary className="small" style={{ cursor: 'pointer', fontWeight: 600 }}>
            Game/math investigation findings
          </summary>
          <div className="ai-analysis-body small" style={{ marginTop: 8 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {test.ai_assessment.game_math_findings}
            </ReactMarkdown>
          </div>
        </details>
      )}

      {test.ai_assessment?.scout_findings && test.ai_assessment.scout_findings.length > 0 && (
        <ScoutFindingsPanel findings={test.ai_assessment.scout_findings} />
      )}

      <h3>Tester verdict</h3>
      {test.ai_verdict && test.tester_verdict && test.ai_verdict !== test.tester_verdict && (
        <p className="muted small">
          AI suggested <strong>{VERDICT_LABEL[test.ai_verdict]}</strong>; tester picked{' '}
          <strong>{VERDICT_LABEL[test.tester_verdict]}</strong> (override).
        </p>
      )}
      <VerdictPicker
        value={effective ?? null}
        disabled={savingPatch}
        onChange={(v) => void patch({ tester_verdict: v })}
      />
      {test.ai_verdict && effective && effective !== test.ai_verdict && (
        <div className="flex-col" style={{ marginTop: 8 }}>
          <label className="small muted" htmlFor="failcat">
            Why was the AI wrong? (captured to improve the system)
          </label>
          <select
            id="failcat"
            value={failureCategory}
            disabled={savingPatch}
            onChange={(e) => {
              const next = e.target.value as FailureCategory | '';
              setFailureCategory(next);
              if (next) {
                void patch({
                  tester_verdict: effective,
                  failure_category: next,
                  tester_justification: justification,
                });
              }
            }}
          >
            <option value="">Select a reason...</option>
            <option value="recognition">
              Recognition - it didn't recognise the mechanic/feature
            </option>
            <option value="observation">
              Observation - it never saw the evidence (vision/video gap)
            </option>
            <option value="judgment">
              Judgment - it saw everything but reasoned wrong
            </option>
            <option value="data">
              Data - the requirement/test it was given was wrong
            </option>
          </select>
        </div>
      )}
      <div className="flex-col" style={{ marginTop: 8 }}>
        <label className="small muted" htmlFor="just">Justification</label>
        <textarea
          id="just"
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          onBlur={() =>
            justification !== (test.tester_justification ?? '') && void patch({ tester_justification: justification })
          }
        />
        <label className="small muted" htmlFor="notes">Tester notes (private)</label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() =>
            notes !== (test.tester_notes ?? '') && void patch({ tester_notes: notes })
          }
        />
      </div>

      <div
        className="row"
        style={{ marginTop: 12, flexWrap: 'wrap', gap: 8 }}
      >
        <button className="btn btn-danger" onClick={openBlankBug}>
          {(test.bug_keys?.length ?? 0) > 0 ? 'Open bug builder (another)' : 'Open bug builder'}
        </button>
        <button
          className="btn"
          onClick={() => void draftBugFromChat()}
          disabled={drafting}
          title="Use the chat history + AI assessment + chat-attached evidence to draft a Jira bug, then open the bug builder pre-filled."
        >
          {drafting ? 'Drafting...' : 'Draft bug from chat'}
        </button>
        {(test.bug_keys?.length ?? 0) > 0 && (
          <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
            <span className="muted small">Linked bugs:</span>
            {test.bug_keys!.map((bk) => (
              <JiraKeyLink
                key={bk}
                keyId={bk}
                className="badge bug small"
                title={`Open bug ${bk} in Jira`}
              />
            ))}
          </div>
        )}
        <span className="spacer" />
        <button
          className="btn btn-primary"
          disabled={!effective || savingPatch}
          onClick={() => void patch({ finalize: true })}
        >
          {test.finalized_at ? 'Re-finalize' : 'Save & finalize'}
        </button>
        <button className="btn" disabled={!hasNext} onClick={onNext}>
          Next
        </button>
      </div>


      <AIChat
        runId={runId}
        testKey={test.test_key}
        initialMessages={chat}
        reanalyzing={analyzing}
        onReanalyzeWithChat={() => void analyze({ withChat: true })}
      />

      {bugOpen && (() => {
        const filterCodes = texec
          ? Array.from(
              new Set(
                [
                  ...(texec.header?.testedJurisdictions ?? []),
                  ...(texec.reportData?.jurisdictions ?? []),
                ].filter(Boolean),
              ),
            )
          : [];
        const filteredReqs = filterRequirementsForJurisdictions(
          test.related_requirements ?? [],
          filterCodes,
        );
        return (
          <BugBuilderModal
            runId={runId}
            test={test}
            gameName={gameName}
            initialDraft={bugDraft ?? undefined}
            filteredRequirements={filteredReqs}
            onClose={() => {
              setBugOpen(false);
              setBugDraft(null);
            }}
            onCreated={(bugKey) => {
              // The websocket `test-update` already updates the
              // review.bug_keys list, but we still set the local state
              // synchronously so the user sees the new bug key without
              // waiting on a round-trip.
              applyTestUpdate(test.test_key, {
                ...test,
                bug_key: bugKey,
                bug_keys: [...(test.bug_keys ?? []), bugKey],
              });
            }}
          />
        );
      })()}

      {rangeView && (() => {
        const total = rangeView.toSecond - rangeView.fromSecond + 1;
        const MAX = 30;
        let seconds: number[];
        let sampled = false;
        if (total <= MAX) {
          seconds = Array.from({ length: total }, (_, i) => rangeView.fromSecond + i);
        } else {
          sampled = true;
          const step = (total - 1) / (MAX - 1);
          seconds = Array.from({ length: MAX }, (_, i) =>
            Math.round(rangeView.fromSecond + i * step),
          );
        }
        return (
          <div className="modal-backdrop image-zoom-backdrop" onClick={() => setRangeView(null)}>
            <div
              className="image-zoom"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 'min(1100px, 92vw)', maxHeight: '88vh', overflow: 'auto' }}
            >
              <div className="row" style={{ marginBottom: 8 }}>
                <strong>
                  {rangeView.slug} @ {formatClock(rangeView.fromSecond)} - {formatClock(rangeView.toSecond)}
                </strong>
                <span className="spacer" />
                <button className="btn btn-ghost" onClick={() => setRangeView(null)}>
                  Close
                </button>
              </div>
              {rangeView.rationale && (
                <p className="muted small" style={{ marginTop: 0, marginBottom: 10 }}>
                  {rangeView.rationale}
                </p>
              )}
              {sampled && (
                <p className="muted small" style={{ marginTop: 0, marginBottom: 10 }}>
                  Showing {MAX} of {total} frames (uniform sample).
                </p>
              )}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                {seconds.map((sec, idx) => {
                  const url = videoFrameUrl(rangeView.slug, sec);
                  const caption = `${rangeView.slug} @ ${formatClock(sec)}`;
                  const sequenceItems: FramesZoomItem[] = seconds.map((s) => ({
                    url: videoFrameUrl(rangeView.slug, s),
                    caption: `${rangeView.slug} @ ${formatClock(s)}`,
                  }));
                  return (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => {
                        setZoomItems(sequenceItems);
                        setZoomIndex(idx);
                      }}
                      title={`Click to zoom frame at ${formatClock(sec)}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 6,
                        padding: 6,
                        background: 'var(--bg-elev)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text)',
                        font: 'inherit',
                        minWidth: 0,
                      }}
                    >
                      <img
                        src={url}
                        alt={caption}
                        loading="lazy"
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          display: 'block',
                          borderRadius: 4,
                          background: 'var(--bg-elev-0)',
                        }}
                        onError={(e) => {
                          const node = e.currentTarget as HTMLImageElement;
                          node.style.display = 'none';
                          const sib = node.nextElementSibling as HTMLElement | null;
                          if (sib) sib.style.display = 'flex';
                        }}
                      />
                      <div
                        style={{
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 8,
                          border: '1px dashed var(--border)',
                          borderRadius: 4,
                          color: 'var(--muted)',
                          fontSize: 11,
                          aspectRatio: '16 / 9',
                        }}
                      >
                        frame {formatClock(sec)}
                      </div>
                      <code
                        className="mono small"
                        style={{
                          textAlign: 'center',
                          color: 'var(--text-dim)',
                          fontSize: 11,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatClock(sec)}
                      </code>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {zoomItems && (
        <FramesZoomModal
          items={zoomItems}
          index={zoomIndex}
          onChange={setZoomIndex}
          onClose={() => setZoomItems(null)}
        />
      )}
    </div>
  );
}
